import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth, roleHomePath } from '@/lib/auth';
import { BrandingProvider } from '@/lib/branding';
import { AppShell } from '@/components/app/AppShell';
import { Toaster } from '@/components/ui/sonner';

import Login from '@/pages/Login';
import NotFound from '@/pages/NotFound';
import ForgotPassword from '@/pages/auth/ForgotPassword';

import OwnerDashboard from '@/pages/owner/Dashboard';
import OwnerProperties from '@/pages/owner/Properties';
import OwnerLeaseExpiry from '@/pages/owner/LeaseExpiry';
import OwnerMaintenance from '@/pages/owner/MaintenanceReview';
import OwnerFinancial from '@/pages/owner/FinancialSummary';
import OwnerReports from '@/pages/owner/Reports';

import ConsultantDashboard from '@/pages/consultant/Dashboard';
import ConsultantProperties from '@/pages/consultant/Properties';
import ConsultantTenants from '@/pages/consultant/Tenants';
import ConsultantLeases from '@/pages/consultant/Leases';
import ConsultantCollections from '@/pages/consultant/Collections';
import ConsultantExpenses from '@/pages/consultant/Expenses';
import ConsultantMaintenance from '@/pages/consultant/Maintenance';
import ConsultantNotices from '@/pages/consultant/Notices';
import ConsultantReports from '@/pages/consultant/Reports';

import TenantDashboard from '@/pages/tenant/Dashboard';
import TenantLease from '@/pages/tenant/Lease';
import TenantPayments from '@/pages/tenant/Payments';
import TenantMaintenance from '@/pages/tenant/Maintenance';
import TenantNotices from '@/pages/tenant/Notices';
import TenantMessages from '@/pages/tenant/Messages';

import AdminDashboard from '@/pages/admin/Dashboard';
import AdminUsers from '@/pages/admin/Users';
import AdminOwners from '@/pages/admin/Owners';
import AdminConsultants from '@/pages/admin/Consultants';
import AdminAgents from '@/pages/admin/Agents';
import AdminTenants from '@/pages/admin/Tenants';
import AdminBranding from '@/pages/admin/Branding';
import AdminAuditLog from '@/pages/admin/AuditLog';
import AdminKYC from '@/pages/admin/KYC';
import AdminAssignments from '@/pages/admin/Assignments';

import AgentDashboard from '@/pages/agent/Dashboard';
import AgentProperties from '@/pages/agent/Properties';
import AgentPropertyDetail from '@/pages/agent/PropertyDetail';

import ProfilePage from '@/pages/Profile';

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={roleHomePath(user.role)} replace />;
  return <AppShell>{children}</AppShell>;
}

function RootRedirect() {
  const { user } = useAuth();
  return <Navigate to={user ? roleHomePath(user.role) : '/login'} replace />;
}

function App() {
  return (
    <BrandingProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            <Route path="/owner" element={<Protected roles={['owner']}><OwnerDashboard /></Protected>} />
            <Route path="/owner/properties" element={<Protected roles={['owner']}><OwnerProperties /></Protected>} />
            <Route path="/owner/lease-expiry" element={<Protected roles={['owner']}><OwnerLeaseExpiry /></Protected>} />
            <Route path="/owner/maintenance" element={<Protected roles={['owner']}><OwnerMaintenance /></Protected>} />
            <Route path="/owner/financial" element={<Protected roles={['owner']}><OwnerFinancial /></Protected>} />
            <Route path="/owner/reports" element={<Protected roles={['owner']}><OwnerReports /></Protected>} />

            <Route path="/consultant" element={<Protected roles={['consultant']}><ConsultantDashboard /></Protected>} />
            <Route path="/consultant/properties" element={<Protected roles={['consultant']}><ConsultantProperties /></Protected>} />
            <Route path="/consultant/tenants" element={<Protected roles={['consultant']}><ConsultantTenants /></Protected>} />
            <Route path="/consultant/leases" element={<Protected roles={['consultant']}><ConsultantLeases /></Protected>} />
            <Route path="/consultant/collections" element={<Protected roles={['consultant']}><ConsultantCollections /></Protected>} />
            <Route path="/consultant/expenses" element={<Protected roles={['consultant']}><ConsultantExpenses /></Protected>} />
            <Route path="/consultant/maintenance" element={<Protected roles={['consultant']}><ConsultantMaintenance /></Protected>} />
            <Route path="/consultant/notices" element={<Protected roles={['consultant']}><ConsultantNotices /></Protected>} />
            <Route path="/consultant/reports" element={<Protected roles={['consultant']}><ConsultantReports /></Protected>} />

            <Route path="/tenant" element={<Protected roles={['tenant']}><TenantDashboard /></Protected>} />
            <Route path="/tenant/lease" element={<Protected roles={['tenant']}><TenantLease /></Protected>} />
            <Route path="/tenant/payments" element={<Protected roles={['tenant']}><TenantPayments /></Protected>} />
            <Route path="/tenant/maintenance" element={<Protected roles={['tenant']}><TenantMaintenance /></Protected>} />
            <Route path="/tenant/notices" element={<Protected roles={['tenant']}><TenantNotices /></Protected>} />
            <Route path="/tenant/messages" element={<Protected roles={['tenant']}><TenantMessages /></Protected>} />

            <Route path="/admin" element={<Protected roles={['admin']}><AdminDashboard /></Protected>} />
            <Route path="/admin/users" element={<Protected roles={['admin']}><AdminUsers /></Protected>} />
            <Route path="/admin/owners" element={<Protected roles={['admin']}><AdminOwners /></Protected>} />
            <Route path="/admin/consultants" element={<Protected roles={['admin']}><AdminConsultants /></Protected>} />
            <Route path="/admin/agents" element={<Protected roles={['admin']}><AdminAgents /></Protected>} />
            <Route path="/admin/tenants" element={<Protected roles={['admin']}><AdminTenants /></Protected>} />
            <Route path="/admin/properties" element={<Protected roles={['admin']}><OwnerProperties /></Protected>} />
            <Route path="/admin/leases" element={<Protected roles={['admin']}><ConsultantLeases /></Protected>} />
            <Route path="/admin/collections" element={<Protected roles={['admin']}><ConsultantCollections /></Protected>} />
            <Route path="/admin/kyc" element={<Protected roles={['admin']}><AdminKYC /></Protected>} />
            <Route path="/admin/assignments" element={<Protected roles={['admin']}><AdminAssignments /></Protected>} />
            <Route path="/admin/branding" element={<Protected roles={['admin']}><AdminBranding /></Protected>} />
            <Route path="/admin/audit-log" element={<Protected roles={['admin']}><AdminAuditLog /></Protected>} />

            <Route path="/agent" element={<Protected roles={['agent']}><AgentDashboard /></Protected>} />
            <Route path="/agent/properties" element={<Protected roles={['agent']}><AgentProperties /></Protected>} />
            <Route path="/agent/listings" element={<Protected roles={['agent']}><AgentProperties /></Protected>} />
            <Route path="/agent/notes" element={<Protected roles={['agent']}><AgentDashboard /></Protected>} />
            <Route path="/agent/properties/:id" element={<Protected roles={['agent']}><AgentPropertyDetail /></Protected>} />

            <Route path="/profile" element={<Protected roles={['admin', 'owner', 'consultant', 'agent', 'tenant']}><ProfilePage /></Protected>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </BrandingProvider>
  );
}

export default App;
